import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SpecialInstructions } from '@core/model/special-instructions';
import { AuthenticationService, BroadcastAppService } from '@core/service';
import { SpecialInstructionsService } from '@core/service/special-instructions.service';
import { AuthorizationData } from '@shared/model';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { MessageService } from 'primeng/api';
import { SubSink } from 'subsink';

@Component({
   // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-special-instructions',
  templateUrl: './special-instructions.component.html',
  styleUrls: ['./special-instructions.component.scss'],
  standalone: false
})
export class SpecialInstructionsComponent implements OnInit, OnDestroy {
  @Input() authorization: AuthorizationData;
  @Input() tenantId: any;
  @Input() outOfNetworkPhone: string;
  specialInstruction: SpecialInstructions;
  saveEnabled = false;
  specialInstructionForm: FormGroup = this.getForm();
  isFormEdit: boolean;
  isLoadingData: boolean;
  hasEditablePermission = false;
  isExternalUser: boolean;
  private subs = new SubSink();

  constructor(
    authenticationService: AuthenticationService, 
    private readonly messageService: MessageService, 
    private readonly securityService: SecurityService, 
    private readonly broadcastService: BroadcastAppService, 
    private readonly specialInstructionsService: SpecialInstructionsService
  ) {
    this.isExternalUser = authenticationService.isExternalUser;
  }

  ngOnInit(): void {
    this.hasEditablePermission = this.securityService.hasAccess(['SpecialInstructions.Write']);
    this.getSpecialInstructions();
  }

  saveSpecialInstructions(): void {
    if (this.isFormEdit) { 
      this.updateSpecialInstruction(); 
    } else {
       this.createSpecialInstruction(); 
      }
  }

  private getForm(): FormGroup {
    return new FormGroup({
      authorizationId: new FormControl(''),
      authorizationNumber: new FormControl(''),
      comment: new FormControl(''),
      templateDescription: new FormControl(''),
      sequenceNumbers: new FormControl('')
    });
  }

  private updateSaveEnabled(): void {
    const noteControl = this.specialInstructionForm.get('comment');
    const noteValue = (noteControl.value || '');
    const originalValue = this.specialInstruction?.comment || '';
    this.saveEnabled = noteValue !== originalValue;
  }

  private getSpecialInstructions(): void {
    this.specialInstructionsService.getSpecialInstructions(this.tenantId, this.authorization.authorization.authorizationId)
      .subscribe((result: SpecialInstructions) => {
        this.specialInstruction = result;
        this.isFormEdit = !!result.authorizationNumber;
        if (this.isFormEdit) { 
          this.specialInstructionForm.patchValue(result); 
        }
        if (this.specialInstruction) { 
          this.saveEnabled = false;
        }
        this.specialInstructionForm.get('authorizationId').setValue(this.authorization.authorization.authorizationId);
        this.initFormWatchers();
      })
  }

  private initFormWatchers(): void {
    this.subs.sink = this.specialInstructionForm.get('comment').valueChanges.subscribe(() => this.updateSaveEnabled());
  }

  private createSpecialInstruction(): void {
    this.isLoadingData = true;
    this.saveEnabled = false;
    const formData = { ...this.specialInstructionForm.getRawValue(), templateDescription: 'Free Text' };
    this.specialInstructionsService.createSpecialInstructions(this.tenantId, formData)
      .subscribe({
        next: (specialInstruction) => {
          this.specialInstruction = specialInstruction;
          this.isFormEdit = true;
          this.broadcastService.notifyNoteCreated();
          this.addSuccessMessage();
        },
        error: () => this.addErrorMessage(),
        complete: () => { this.isLoadingData = false; }
      });
  }

  private updateSpecialInstruction(): void {
    this.isLoadingData = true;
    this.saveEnabled = false;
    const formData = { ...this.specialInstructionForm.getRawValue(), templateDescription: 'Free Text' };
    this.specialInstructionsService.updateSpecialInstructions(this.tenantId, formData)
      .subscribe({
        next: (specialInstruction) => {
          this.specialInstruction = specialInstruction;
          this.broadcastService.notifyNoteCreated();
          this.addSuccessMessage();
        },
        error: () => this.addErrorMessage(),
        complete: () => { this.isLoadingData = false; }
      });
  }

  private addSuccessMessage() {
    this.messageService.add({ 
      severity: 'success', 
      summary: 'Special Instruction Successfully Saved', 
      life: 3000 
    });
  }

  private addErrorMessage() {
    this.messageService.add({ 
      severity: 'error',
       summary: 'Special Instruction Save Failed', 
       life: 3000
       });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
